/**
 * What a live session's special surfaces show (D282), in the workbench's own words, DOM-free: its tool calls as
 * HeroUI Pro's `ChatTool` reads them, an approval card's kind, position and answers, and the task strip's counts and
 * status words. The console's live view and the phone's both draw from here, so a session reads the same in the
 * workbench, in a browser and on a phone.
 *
 * Copied from three workbench files at 52bc328e (next/0.89.20), each section saying which, with the types narrowed to
 * what the live stream carries (`@aioe/shared` remoteControl.ts): the tool-call part of
 * webview-ui/src/components/facilitator/chatPartsModel.ts, the card words of
 * webview-ui/src/components/agents/approvalModel.ts, and the task strip's words of
 * webview-ui/src/facilitator/conversationModel.ts. At 9a76e5e2 (next/0.89.21, D372): the task strip's person line
 * (conversationModel.ts), the answers for a request its agent can no longer act on (approvalModel.ts), the dividers'
 * words and a turn's changed files (webview-ui/src/facilitator/conversationActionsModel.ts, over the stream's
 * `changes` shape), and the usage limit's banner lines (webview-ui/src/components/agents/handOffModel.ts with
 * src/acp/agentFailure.ts's sentences). The adapters at the end (a live `tool` event as an activity, a transcript's
 * tool runs grouped, by agent) are AIOE's own.
 */
import { toolPhrase, toolSentence, toolStepOf } from './toolSummaryModel.js';
import { limitLiftsWords } from '../protocol/agentState.js';
/** How many calls one group lists before summing the rest up. */
export const MAX_TOOL_CALLS = 8;
/** The workbench's activity status as `ChatTool` state. `pending` has been asked for and not started. */
export function toolStateOf(status) {
    switch (status) {
        case 'active':
            return 'input-available';
        case 'pending':
            return 'input-streaming';
        case 'error':
            return 'output-error';
        default:
            return 'output-available';
    }
}
/**
 * One activity as a tool call. `running` says whether the agent behind it is
 * still mid-turn: a persisted "active" item from a session that has since
 * stopped is shown as finished, not as a spinner that never ends.
 */
export function toolCallView(s, index, running = true) {
    const step = toolStepOf(s);
    const live = s.status === 'active' && running;
    const state = s.status === 'active' && !running ? 'output-available' : toolStateOf(s.status);
    const name = toolPhrase(step, live);
    const detail = step.title.trim();
    return {
        key: `${s.timestamp}-${index}`,
        kind: step.kind,
        name,
        state,
        detail: detail && !name.endsWith(detail) ? detail : undefined,
        live,
    };
}
/**
 * A run of tool calls: the last `MAX_TOOL_CALLS`, and one line for the lot
 * (D120). While a call runs the line is that call ("Running npm test"); when
 * it ends and the turn goes on (`tail`: this is the turn's latest group) the
 * line keeps it in the past tense ("Ran npm test") rather than vanishing;
 * once the turn is over it is a sentence ("Read 3 files and ran 2 commands").
 */
export function toolGroupView(items, running = true, tail = false) {
    const all = items.map((s, i) => toolCallView(s, i, running));
    const calls = all.slice(-MAX_TOOL_CALLS);
    const current = [...all].reverse().find((c) => c.live);
    const failed = all.filter((c) => c.state === 'output-error').length;
    const summary = current ? current.name : running && tail && all.length ? all[all.length - 1].name : toolSentence(items.map(toolStepOf));
    return { calls, hidden: all.length - calls.length, summary, live: !!current, failed };
}
export const REQUEST_KIND_LABEL = {
    command: 'Command',
    'file-change': 'File change',
    delete: 'Delete',
    read: 'Read',
    fetch: 'Web fetch',
    tool: 'Tool',
    other: 'Action',
};
/** The card's kind from what the host wrote down, else from the ACP tool kind (an older host). */
export function requestKind(written, toolKind) {
    if (written)
        return written;
    switch (String(toolKind ?? '').toLowerCase()) {
        case 'execute':
            return 'command';
        case 'edit':
        case 'move':
            return 'file-change';
        case 'delete':
            return 'delete';
        case 'read':
        case 'search':
        case 'think':
            return 'read';
        case 'fetch':
            return 'fetch';
        default:
            return 'other';
    }
}
/** "2/3": this request's place among the same agent's waiting ones, or undefined when it is alone. */
export function positionLabel(item, all) {
    const mine = all.filter((r) => r.sessionId === item.sessionId);
    if (mine.length < 2)
        return undefined;
    const i = mine.findIndex((r) => r.id === item.id);
    return i === -1 ? undefined : `${i + 1}/${mine.length}`;
}
/** The request's own allow-for-the-session option (ACP `allow_always`), when it offers one. */
export function sessionAllowOption(options) {
    const o = options?.find((x) => x.kind === 'allow_always');
    return o ? (o.optionId ?? o.id) : undefined;
}
/** What each button on a card sends (`permissions.resolve`). */
export const ANSWERS = {
    approve: { decision: 'allow' },
    decline: { decision: 'deny' },
    session: (optionId) => ({ decision: 'allow', optionId }),
    askAgain: { decision: 'allow', optionId: 'ask-again' },
    dismiss: { decision: 'deny', optionId: 'dismiss' },
};
/** Can this request still be approved, or only asked again or dismissed? */
export function isLive(answerable) {
    return answerable === undefined || answerable === 'live';
}
// ── The task strip (conversationModel.ts) ───────────────────────────────
export const TASK_STATUS_META = {
    backlog: { label: 'backlog', color: 'default' },
    todo: { label: 'to do', color: 'default' },
    'in-progress': { label: 'in progress', color: 'accent' },
    review: { label: 'review', color: 'warning' },
    done: { label: 'done', color: 'success' },
    blocked: { label: 'blocked', color: 'danger' },
};
/** The task strip's counts: done, running, needing attention, and still active (not done). */
export function taskCounts(tasks) {
    return {
        done: tasks.filter((t) => t.status === 'done').length,
        running: tasks.filter((t) => t.status === 'in-progress' || t.status === 'review').length,
        attention: tasks.filter((t) => t.status === 'blocked' || t.needsHuman).length,
        // Nothing to pause once everything has landed.
        active: tasks.filter((t) => t.status !== 'done').length,
    };
}
/**
 * Who the run waits for (D236): "Waiting for JP: T-12, T-14" for the tasks people on the team have and have
 * not finished. A person's task is waiting, not stalled. Undefined when no person has one.
 */
export function personWaitLine(tasks) {
    const byPerson = new Map();
    for (const t of tasks) {
        if (!t.forPerson || !t.assignee || t.status === 'done')
            continue;
        byPerson.set(t.assignee, [...(byPerson.get(t.assignee) ?? []), t.id]);
    }
    if (!byPerson.size)
        return undefined;
    return [...byPerson].map(([who, ids]) => `Waiting for ${who}: ${ids.join(', ')}`).join(' · ');
}
// ── Dividers and a turn's changes (conversationActionsModel.ts) ─────────
/** A divider's words: handed over (D137), forked from, went back (D126). */
export function markerWords(marker) {
    if (marker.kind === 'handoff') {
        const text = marker.from && marker.from !== marker.to ? `Handed over from ${marker.from} to ${marker.to ?? 'another agent'}` : `Now with ${marker.to ?? 'another agent'}`;
        if (marker.carried === undefined)
            return { text };
        const omitted = marker.omitted ? `; ${marker.omitted} more it can read if it needs them` : '';
        return { text, detail: `${marker.carried} earlier message${marker.carried === 1 ? '' : 's'} carried over${omitted}` };
    }
    if (marker.kind === 'forked-from')
        return { text: `Forked from “${marker.title ?? 'another conversation'}”` };
    if (marker.kind === 'research-wrapup')
        return { text: 'Research done' };
    if (marker.kind === 'plan-wrapup')
        return { text: 'Wrap-up' };
    return { text: 'Went back to here', detail: 'The agent starts again with the conversation up to this point.' };
}
/** "3 changed files", with the lines added and removed. */
export function turnChangesSummary(files) {
    const insertions = files.reduce((n, f) => n + f.added, 0);
    const deletions = files.reduce((n, f) => n + f.removed, 0);
    return { count: files.length, insertions, deletions, label: `${files.length} changed file${files.length === 1 ? '' : 's'}` };
}
/** A compact tree: files grouped by folder, folders and files in name order. */
export function changedFileTree(files) {
    const groups = new Map();
    for (const f of files) {
        const cut = f.path.lastIndexOf('/');
        const dir = cut < 0 ? '' : f.path.slice(0, cut);
        const group = groups.get(dir) ?? { dir, files: [] };
        group.files.push({ ...f, name: cut < 0 ? f.path : f.path.slice(cut + 1) });
        groups.set(dir, group);
    }
    return [...groups.values()]
        .sort((a, b) => a.dir.localeCompare(b.dir))
        .map((g) => ({ ...g, files: g.files.sort((a, b) => a.name.localeCompare(b.name)) }));
}
// ── The usage limit's banner (handOffModel.ts, agentFailure.ts) ─────────
/** "in 20 minutes" under 90 minutes, "at 11:40" beyond (the workbench's describeReset). */
export function describeReset(at, now = Date.now()) {
    const ms = at - now;
    if (ms <= 0)
        return 'now';
    if (ms < 90 * 60_000) {
        const mins = Math.max(1, Math.round(ms / 60_000));
        return `in ${mins} minute${mins === 1 ? '' : 's'}`;
    }
    return `at ${new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}
/**
 * The banner's lines for an agent waiting for its usage limit, in the window's words: what happened, what happens
 * next, and a countdown re-worded each minute (`now` moves). A wait, never an error. `handOff`: the workbench is
 * handing the work to another account or runtime.
 */
export function limitBannerLines(limit, now = Date.now(), agent) {
    const at = limit.resetsAt ? Date.parse(limit.resetsAt) : NaN;
    const resetsAt = Number.isFinite(at) ? at : undefined;
    const who = agent?.trim() ? `${agent.trim()}'s agent` : 'The agent';
    const summary = resetsAt ? `${who} reached its usage limit. It lifts ${describeReset(resetsAt, now)}.` : `${who} reached its usage limit for now.`;
    const action = limit.handOff
        ? 'It is being handed to another account or runtime, so the work need not wait.'
        : resetsAt
            ? 'Nothing to do: the agent is restarted when the limit lifts and picks its task back up.'
            : 'The agent is retried periodically until the limit lifts. Restart it yourself to try sooner.';
    const lifts = limitLiftsWords(resetsAt, now);
    return { summary, action, ...(lifts ? { lifts: `Limit ${lifts}.` } : {}) };
}
// ── AIOE's adapters for the live stream ─────────────────────────────────
/** A task's status word and the colour it takes; one the strip does not know reads as itself. */
export function taskStatusMeta(status) {
    const s = status?.trim() ?? '';
    return TASK_STATUS_META[s] ?? { label: s ? s.replace(/[_-]+/g, ' ') : 'to do', color: 'default' };
}
/** "Tasks from this chat · 2/5 done · 1 running · 1 needs attention": the strip's line, as the workbench writes it. */
export function taskStripLine(tasks) {
    const c = taskCounts(tasks.map((t) => ({ status: t.status ?? '', ...(t.needsHuman ? { needsHuman: true } : {}) })));
    return `${c.done}/${tasks.length} done${c.running > 0 ? ` · ${c.running} running` : ''}${c.attention > 0 ? ` · ${c.attention} need attention` : ''}`;
}
/** A live `tool` event's state (the ACP tool-call status) as an activity's. */
export function activityStatusOf(state) {
    switch (state) {
        case 'pending':
            return 'pending';
        case 'in_progress':
        case 'running':
            return 'active';
        case 'failed':
        case 'error':
            return 'error';
        case undefined:
        case '':
            return 'active';
        default:
            return 'done';
    }
}
/**
 * A live `tool` event as the activity the workbench's words read: the ACP kind as the label, the title as the detail.
 * A call that failed (`failed`, D372) is an error whatever its state says.
 */
export function liveToolActivity(t) {
    return { kind: 'tool', label: t.toolKind ?? '', detail: t.title ?? '', status: t.failed ? 'error' : activityStatusOf(t.state), timestamp: t.at };
}
/**
 * A run of tool calls split by the agent that made each (D372's `agent`), in the order each agent first appears: a
 * Team Work run's members each get their own line. Calls that name no agent stay together.
 */
export function toolRunsByAgent(items) {
    const out = [];
    for (const it of items) {
        const agent = it.agent?.trim() || undefined;
        const group = out.find((g) => g.agent === agent);
        if (group)
            group.items.push(it);
        else
            out.push({ ...(agent ? { agent } : {}), items: [it] });
    }
    return out;
}
export function groupToolRuns(items, isTool, keyOf) {
    const out = [];
    for (const it of items) {
        if (!isTool(it)) {
            out.push({ kind: 'item', item: it });
            continue;
        }
        const last = out.at(-1);
        if (last?.kind === 'tools')
            last.items.push(it);
        else
            out.push({ kind: 'tools', key: keyOf(it), items: [it] });
    }
    return out;
}
// ── The composer's Stop (sendActionModel.ts) ────────────────────────────
/**
 * What Stop does, in the tooltip's words: the run stops, the session stays (the workbench's `stopRunHint`, its modes
 * named as the live stream names them: Team Work is `teamwork`, an agent session of its own reads as Chat).
 */
export function stopRunHint(mode) {
    if (mode === 'teamwork')
        return 'Stop this run — its tasks wait until you ask again. Other work on the team carries on';
    if (mode === 'research')
        return 'Stop this run — its researchers stop, and what they found so far is kept. Other work carries on';
    return 'Stop this run — the agent stops where it is. Other work carries on';
}
/**
 * The ACP kind of the tool call an approval waits on, read from the newest call of the same title the stream carried:
 * the fallback for a workbench from before D372, whose approvals name no `toolKind` or `requestKind`.
 */
export function approvalToolKind(title, tools) {
    const t = title?.trim();
    if (!t)
        return undefined;
    for (let i = tools.length - 1; i >= 0; i--)
        if (tools[i].title?.trim() === t && tools[i].toolKind)
            return tools[i].toolKind;
    return undefined;
}
//# sourceMappingURL=liveSessionParts.js.map